import {
  keepPreviousData,
  skipToken,
  useQuery,
  type QueryFunctionContext,
} from '@tanstack/react-query';
import { useCurrentSimDataId } from '../SimViewer/useCurrentSimDataId';
import { dataApiClient } from '../../infrastructure/dataApi/client';
import { useApiDataFilter } from '../SimViewer/useApiDataFilter';
import { useAggregationConfig } from '../SimViewer/useAggregationConfig';
import { includes } from '../../utils/array';
import type { Trip } from '../../types/domain';

type UseTripsData = {
  isLoading: boolean;
  trips: Trip[];
};

const EMPTY_DATA = [] as const satisfies Trip[];

const useTripsData = (): UseTripsData => {
  const { currentSimDataId } = useCurrentSimDataId();
  const { apiFilter } = useApiDataFilter();
  const { aggregationConfig } = useAggregationConfig();

  const isRequired =
    (includes(['grid_heatmap', 'heatmap'], aggregationConfig?.type) &&
      includes(['start', 'end'], aggregationConfig?.dimension)) ||
    aggregationConfig?.type === 'arcs';

  const { data, isFetching } = useQuery({
    queryKey: ['trips', currentSimDataId, apiFilter],
    queryFn:
      currentSimDataId && isRequired
        ? async ({ signal }: QueryFunctionContext): Promise<Trip[]> => {
            return dataApiClient.getTrips(currentSimDataId, apiFilter, signal);
          }
        : skipToken,
    placeholderData: keepPreviousData,
    meta: { dataType: 'aggregation' },
  });

  return {
    isLoading: isFetching,
    trips: data && isRequired ? data : EMPTY_DATA,
  };
};

export { useTripsData };
